/**
 * The card's padding in em of its type (`PreviewBody`, mockup `.p`): sides,
 * top and bottom. Every copy reads it: `CARD_PADDING` below, the opened
 * reading heights (`lib/focus/focus-opened-size.ts`), the opened
 * window's `--open-pad-*` (`components/focus/frame-window-card.css`, at
 * `--open-fs`) and a Pinned tile's (`components/wall/wall-surface.css`, at
 * `--wall-fs`); their tests pin the values to this.
 */
export const CARD_PADDING_EM = {
  inline: 1.6,
  blockStart: 1.55,
  blockEnd: 1.7,
} as const;

/**
 * `CARD_PADDING_EM` as classes. Tailwind reads class names literally, so
 * they are spelled out here; `frame-content-padding.test.ts` keeps them
 * equal to the triple.
 */
export const CARD_PADDING = "px-[1.6em] pt-[1.55em] pb-[1.7em]";

/**
 * A frame body's padding: the card's margins in an opened window or a
 * Pinned tile (`--open-pad-*`; 0 for media), and the canvas-era `px-3 py-2`
 * everywhere else.
 */
export const FRAME_CONTENT_PADDING =
  "px-[var(--open-pad-inline,0.75rem)] pt-[var(--open-pad-block-start,0.5rem)] pb-[var(--open-pad-block-end,0.5rem)]";

/**
 * A calm title (`FrameCalmTitle`) at the card's inline margin
 * (`--open-pad-*`: `frame-window-card.css` in an opened window,
 * `wall-surface.css` on a Pinned tile).
 */
export const CARD_TITLE_INSET = "px-[var(--open-pad-inline)]";

/**
 * A body of rows under that title: rows and their labels pad themselves by
 * 0.5rem (`PocketRow`, `PocketSection`), so the body pads that much less and
 * their text sits at the title's margin; 0.75em below the title, the card's
 * bottom margin below the last row.
 */
export const CARD_ROWS_INSET =
  "px-[calc(var(--open-pad-inline)-0.5rem)] pt-[0.75em] pb-[var(--open-pad-block-end)]";

/**
 * A frame body drawn as its card (`PreviewBody`, `CodeWindow`; Part 10):
 * the card's em type at the opened window's `--wall-fs` (17px), and 15px on
 * the canvas, where code and the terminal then read at 12px as before. The
 * card part brings the card's margins, so the glass content padding is 0.
 */
export const CARD_BODY_CONTENT =
  "[--denk-frame-content-padding-block-end:0px] [--denk-frame-content-padding-block-start:0px] [--denk-frame-content-padding-inline:0px] [font-size:var(--wall-fs,15px)] leading-[1.45]";
